import { prepareHeaders } from '@app/app/lib/api';
import { serverPath } from '@app/utils/server-path';

export interface InfographicOperation {
  request_id: string;
  workspace_id: number;
  slide_id: number;
  kind: 'edit' | 'upload' | 'restore' | 'restyle';
  status: 'queued' | 'running' | 'applied' | 'failed' | 'cancelled' | 'conflict';
  stage: string;
  sequence: number;
  base_revision: number;
  applied_revision: number | null;
  before_version_id: string | null;
  result_version_id: string | null;
  error_code: string;
}

export interface InfographicMetadata {
  target_id: string;
  version_id?: string;
  source: 'generated' | 'uploaded';
  can_edit: boolean;
  can_upload: boolean;
  token_cost: number;
  billing_mode: 'tokens' | 'unlimited';
  active_operation?: InfographicOperation | null;
  latest_operation?: InfographicOperation | null;
}

export interface InfographicSnapshot {
  snapshot_id: string;
  content_revision: number;
  preview: string;
  token_cost: number;
  background_mode: string;
  background_description: string;
  upload_frame?: { width: number; height: number; background: string | null };
}

export class InfographicRequestError extends Error {
  constructor(
    public code: string,
    public status: number,
  ) {
    super(code);
  }
}

export async function editorRequest<T>(
  url: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const headers = new Headers(prepareHeaders());
  if (body instanceof FormData) headers.delete('Content-Type');
  const response = await fetch(url, {
    method: body === undefined ? 'GET' : 'POST',
    headers,
    signal,
    body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || !result.status) {
    throw new InfographicRequestError(
      result.result?.code || result.errors || 'INFOGRAPHIC_REQUEST_FAILED',
      response.status,
    );
  }
  return result.result as T;
}

export function infographicRequest<T>(
  workspaceId: string,
  slideId: string,
  action: string,
  body?: unknown,
  signal?: AbortSignal,
) {
  return editorRequest<T>(
    serverPath.workspace.infographic(workspaceId, slideId, action),
    body,
    signal,
  );
}

export function saveCanvasConfig(workspaceId: string, slideId: string, body: unknown) {
  return editorRequest<{ content_revision: number }>(
    serverPath.workspace.saveSlideConfig(workspaceId, slideId),
    body,
  );
}
